import { useMemo } from 'react';
import { RuleStatus, useListRulesQuery } from '../../hinge-connect/rules/types';
import { isFileIngestionRule } from '../constants/file-ingestion.constant';

export type Provider = {
  source: string;
  rulesCount: number;
  fileIngestionRulesCount: number;
  lastUpdated: string;
  status: 'active' | 'inactive';
  hasPublishedRules: boolean;
};

export type UseProvidersReturn = {
  providers: Provider[];
  loading: boolean;
  error: Error | undefined;
  refetch: () => void;
};

/**
 * Hook to fetch and organize rules data as providers for Claims Ingestion
 * Only returns providers that have file ingestion rules (identified by specific resource types)
 */
export const useProviders = (): UseProvidersReturn => {
  const {
    data: listRulesData,
    loading,
    error,
    refetch,
  } = useListRulesQuery({
    variables: {
      listRulesInput: {
        // Fetch all rules to analyze providers
      },
    },
  });

  const providers = useMemo(() => {
    if (!listRulesData?.listRules?.rules) {
      return [];
    }

    const providerMap = new Map<
      string,
      {
        rules: typeof listRulesData.listRules.rules;
        lastUpdated: string;
      }
    >();

    // Group rules by source (provider), filtering for file ingestion rules only
    listRulesData.listRules.rules.forEach(rule => {
      // Only process rules that have file ingestion resource types
      if (!isFileIngestionRule(rule.resourceTypes)) {
        return;
      }

      const source = rule.source || 'Unknown';
      const updatedAt = rule.updatedAt || new Date().toISOString();

      if (!providerMap.has(source)) {
        providerMap.set(source, {
          rules: [],
          lastUpdated: updatedAt,
        });
      }

      const providerData = providerMap.get(source);
      if (providerData) {
        providerData.rules.push(rule);

        // Keep the most recent update time
        if (new Date(updatedAt) > new Date(providerData.lastUpdated)) {
          providerData.lastUpdated = updatedAt;
        }
      }
    });

    // Convert map to Provider array
    return Array.from(providerMap.entries())
      .map(([source, data]) => {
        const hasPublishedRules = data.rules.some(
          rule => rule.status === RuleStatus.Published,
        );

        // Count total rules and file ingestion rules specifically
        const allFileIngestionRules = data.rules.filter(rule =>
          isFileIngestionRule(rule.resourceTypes),
        );

        return {
          source,
          rulesCount: data.rules.length,
          fileIngestionRulesCount: allFileIngestionRules.length,
          lastUpdated: data.lastUpdated,
          status: hasPublishedRules
            ? ('active' as const)
            : ('inactive' as const),
          hasPublishedRules,
        };
      })
      .sort((a, b) => {
        // Sort by status (active first) then by last updated (newest first)
        if (a.status !== b.status) {
          return a.status === 'active' ? -1 : 1;
        }
        return (
          new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime()
        );
      });
  }, [listRulesData]);

  return {
    providers,
    loading,
    error,
    refetch,
  };
};
